import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, User, UserPlus } from 'lucide-react';
import Button from '../common/Button';
import Input from '../common/Input';
import { fieldAPI, eventAPI } from '../../api/endpoints';
import { getActiveUniqueFields } from '../../utils/fieldHelpers';
import { validateAttendeeDataFormat } from '../../utils/validateDataFormat';
import CountrySelect from '../common/CountrySelect';

const AttendeeModal = ({
  isOpen,
  onClose,
  onSubmit,
  attendee = null, // null for add mode, attendee object for edit mode
  loading,
  selectedEvent
}) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [emailConsent, setEmailConsent] = useState(true); // Default to true for admin-created attendees

  // Determine if we're in edit mode
  const isEditMode = !!attendee;
  const modalTitle = isEditMode ? 'Edit Attendee' : 'Register New Attendee';
  const submitButtonText = isEditMode ? 'Save Changes' : 'Register Attendee';
  const IconComponent = isEditMode ? User : UserPlus;

  // Extract event ID for API calls
  const eventId = selectedEvent?.id || selectedEvent;

  // Fetch event data to get fresh email notification settings
  const { data: eventData } = useQuery({
    queryKey: ['event-details', eventId],
    queryFn: async () => {
      if (!eventId) return null;
      const response = await eventAPI.getById(eventId);
      return response.data;
    },
    enabled: !!eventId && isOpen,
  });

  // Fetch custom fields for selected event
  const { data: fieldsData, isLoading: fieldsLoading } = useQuery({
    queryKey: ['fields', eventId],
    queryFn: async () => {
      if (!eventId) return { data: [] };
      const response = await fieldAPI.getAll(eventId);
      return response.data;
    },
    enabled: !!eventId && isOpen,
  });

  // Populate form with existing attendee data (edit mode) or reset (add mode)
  useEffect(() => {
    if (isOpen) {
      if (isEditMode && attendee && fieldsData) {
        const initialData = {};
        const activeFields = getActiveUniqueFields(fieldsData);

        console.log('🔍 AttendeeModal - Populating form data for edit:', {
          attendee,
          activeFields: activeFields.map(f => ({ id: f.id, name: f.field_name, label: f.field_label })),
          attendeeData: attendee.data
        });

        // Populate form data from attendee.data array
        if (attendee.data && Array.isArray(attendee.data) && attendee.data.length > 0) {
          attendee.data.forEach(item => {
            // Handle different data structures - be more flexible
            const fieldId = item.custom_field_id || item.field_id || item.field?.id;
            const fieldValue = item.value || item.field_value || '';

            if (fieldId && fieldValue !== null && fieldValue !== undefined) {
              initialData[fieldId] = fieldValue;
              console.log(`✅ Populated field ${fieldId} with value: "${fieldValue}"`);
            }
          });
        }

        // Also try to populate from direct attendee properties as fallback
        activeFields.forEach(field => {
          // Only populate if we don't already have data for this field
          if (!initialData[field.id]) {
            let value = null;

            switch (field.field_name) {
              case 'full_name':
                value = attendee.full_name;
                break;
              case 'email':
                value = attendee.email;
                break;
              case 'phone':
                value = attendee.phone;
                break;
              case 'organization':
                value = attendee.organization;
                break;
              case 'position':
                value = attendee.position;
                break;
              default:
                // Check if attendee has a property matching the field name
                value = attendee[field.field_name];
                break;
            }

            if (value !== null && value !== undefined && value !== '') {
              initialData[field.id] = value;
              console.log(`✅ Fallback populated field ${field.field_name} (${field.id}) with value: "${value}"`);
            }
          }
        });

        console.log('🔍 AttendeeModal - Final form data:', initialData);
        setFormData(initialData);
      } else {
        // Add mode or no attendee data - reset form
        setFormData({});
        setEmailConsent(true); // Reset to true by default for admin-created attendees
      }
      setErrors({});
    }
  }, [isOpen, isEditMode, attendee, fieldsData]);

  const handleChange = (fieldId, value) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
    if (errors[fieldId]) {
      setErrors(prev => ({ ...prev, [fieldId]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    const fields = getActiveUniqueFields(fieldsData);

    // Debug logging to see what fields we're validating
    console.log('🔍 AttendeeModal validation fields:', fields.map(f => ({
      name: f.field_name,
      label: f.field_label,
      active: f.is_active,
      required: f.is_required,
      type: f.field_type,
      value: formData[f.id]
    })));

    fields.forEach(field => {
      // Determine if field is required
      // Rule: Only active fields can be required
      const isFieldRequired = field.is_active === true && (field.is_required === true || field.is_mandatory_by_event === true);

      if (isFieldRequired && !formData[field.id]) {
        console.log(`❌ Field required but empty: ${field.field_name} (${field.field_label}), active: ${field.is_active}, required: ${field.is_required}`);
        newErrors[field.id] = `${field.field_label} is required`;
      }

      // Email validation
      if (field.field_type === 'email' && formData[field.id]) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData[field.id])) {
          newErrors[field.id] = 'Invalid email address';
        }
      }

      // Phone validation
      if (field.field_type === 'phone' && formData[field.id]) {
        const phoneRegex = /^[\d\s-+()]+$/;
        if (!phoneRegex.test(formData[field.id])) {
          newErrors[field.id] = 'Invalid phone number';
        }
      }
    });

    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!eventId) {
      setErrors({ event: 'Please select an event' });
      return;
    }

    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Format data for submission using active fields only
    const uniqueFields = getActiveUniqueFields(fieldsData);

    // Validate and format data for Laravel backend (same as SelfRegistration)
    // Pass isUpdate flag to ensure empty fields are included for updates (allows clearing fields)
    const validation = validateAttendeeDataFormat(formData, uniqueFields, isEditMode);

    if (!validation.isValid) {
      console.error('❌ Data validation failed:', validation.errors);
      setErrors({ general: validation.errors.join(', ') });
      return;
    }

    // Add any additional data that might be expected by the backend
    const attendeeData = {
      ...validation.data,
      email_consent: uniqueFields.some(field => field.field_type === 'email') && eventData?.enable_email_notifications ? emailConsent : false
    };

    console.log(`🚀 AttendeeModal (${isEditMode ? 'Edit' : 'Add'}) - Sending validated attendee data:`, attendeeData);
    console.log(`🚀 AttendeeModal - Original form data:`, formData);
    console.log(`🚀 AttendeeModal - Validation result:`, validation);
    console.log(`🚀 AttendeeModal - Selected event:`, selectedEvent);
    console.log(`🚀 AttendeeModal - Unique fields:`, uniqueFields.map(f => ({ id: f.id, name: f.field_name, active: f.is_active })));

    // Call onSubmit with the validated data
    // For edit mode, pass attendee ID as first parameter
    if (isEditMode) {
      onSubmit(attendee.id, attendeeData);
    } else {
      onSubmit(attendeeData);
    }

    // Clear form after submission
    setFormData({});
    setErrors({});
    setEmailConsent(true); // Reset to true by default for admin-created attendees
  };

  const handleClose = () => {
    setFormData({});
    setErrors({});
    setEmailConsent(true); // Reset to true by default for admin-created attendees
    onClose();
  };

  const renderField = (field) => {
    const commonProps = {
      label: field.field_label,
      value: formData[field.id] || '',
      onChange: (e) => handleChange(field.id, e.target.value),
      error: errors[field.id],
      required: field.is_required,
      placeholder: field.placeholder || `Enter ${field.field_label.toLowerCase()}`,
    };

    switch (field.field_type) {
      case 'text':
        return <Input key={field.id} {...commonProps} type="text" />;

      case 'email':
        return <Input key={field.id} {...commonProps} type="email" />;

      case 'phone':
        return <Input key={field.id} {...commonProps} type="tel" />;

      case 'number':
        return <Input key={field.id} {...commonProps} type="number" />;

      case 'textarea':
        return (
          <div key={field.id}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {field.field_label}
              {field.is_required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <textarea
              value={formData[field.id] || ''}
              onChange={(e) => handleChange(field.id, e.target.value)}
              placeholder={commonProps.placeholder}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {errors[field.id] && (
              <p className="text-red-500 text-sm mt-1">{errors[field.id]}</p>
            )}
          </div>
        );

      case 'dropdown':
      case 'select':
        // Parse options safely - handle both field.options and field.field_options
        let options = [];
        const optionsData = field.field_options || field.options;
        if (optionsData) {
          if (typeof optionsData === 'string') {
            try {
              options = JSON.parse(optionsData);
            } catch (e) {
              options = [];
            }
          } else if (Array.isArray(optionsData)) {
            options = optionsData;
          }
        }
        return (
          <div key={field.id}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {field.field_label}
              {field.is_required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <select
              value={formData[field.id] || ''}
              onChange={(e) => handleChange(field.id, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Select {field.field_label}</option>
              {options.map((option, idx) => (
                <option key={idx} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {errors[field.id] && (
              <p className="text-red-500 text-sm mt-1">{errors[field.id]}</p>
            )}
          </div>
        );

      case 'checkbox':
        return (
          <div key={field.id} className="flex items-center">
            <input
              type="checkbox"
              id={field.id}
              checked={formData[field.id] === 'true' || formData[field.id] === true}
              onChange={(e) => handleChange(field.id, e.target.checked.toString())}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor={field.id} className="ml-2 block text-sm text-gray-700">
              {field.field_label}
              {field.is_required && <span className="text-red-500 ml-1">*</span>}
            </label>
          </div>
        );

      case 'date':
        return <Input key={field.id} {...commonProps} type="date" />;

      case 'country':
        return (
          <CountrySelect
            key={field.id}
            {...commonProps}
            onChange={(val) => handleChange(field.id, val)}
          />
        );

      default:
        return <Input key={field.id} {...commonProps} type="text" />;
    }
  };

  if (!isOpen) return null;

  const allFields = getActiveUniqueFields(fieldsData);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl transform transition-all max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white flex items-center justify-between p-6 border-b z-10">
            <div className="flex items-center gap-2">
              <IconComponent className="h-6 w-6 text-primary-600" />
              <h3 className="text-xl font-semibold text-gray-900">{modalTitle}</h3>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={loading}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Event Info (Edit Mode Only) */}
            {isEditMode && attendee?.event && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <span className="font-medium">Event:</span> {attendee.event.name}
                </p>
              </div>
            )}

            {/* General Error Display */}
            {errors.general && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800 text-sm">{errors.general}</p>
              </div>
            )}

            {/* Dynamic Fields */}
            {eventId ? (
              <>
                {fieldsLoading ? (
                  <div className="text-center py-8 text-gray-500">
                    Loading registration fields...
                  </div>
                ) : allFields.length > 0 ? (
                  <div className="space-y-4">
                    <div className="border-t pt-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-4">
                        Attendee Information
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {allFields.map(field => renderField(field))}
                      </div>
                    </div>

                    {/* Email consent removed - admin assumes consent for attendees they create */}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <p className="text-sm text-yellow-600 mt-1">
                      Please configure custom fields first.
                    </p>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8 text-gray-500">
                Please select an event to load registration fields.
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t sticky bottom-0 bg-white">
              <Button
                type="button"
                variant="secondary"
                onClick={handleClose}
                className="flex-1"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1"
                loading={loading}
                disabled={!eventId || allFields.length === 0}
              >
                {submitButtonText}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AttendeeModal;
